import React, { useState, useEffect } from 'react';
import { subscriptionsAPI, categoriesAPI, emailAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Mail, Trash2, Users, Send, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

export default function AdminNewsletter() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', template_id: '' });
  const [emailHistory, setEmailHistory] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subRes, catRes] = await Promise.all([
        subscriptionsAPI.list({ limit: 100 }),
        categoriesAPI.list(),
      ]);
      setSubscriptions(subRes.data.subscriptions || []);
      setTotal(subRes.data.total || 0);
      setCategories(catRes.data.categories || []);
      // Load email history
      emailAPI.history().then(r => setEmailHistory(r.data.emails || [])).catch(() => {});
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Unsubscribe ${email}?`)) return;
    try {
      await subscriptionsAPI.unsubscribe(email);
      toast.success('Unsubscribed');
      loadData();
    } catch { toast.error('Failed'); }
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat?.name || id;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Newsletter</h1>
          <p className="text-sm text-[#5B616E] mt-1">{total} subscribers</p>
        </div>
        <Button className="bg-[#1E5A8A] text-white hover:bg-[#1E5A8A]/90" onClick={() => setShowSend(true)}>
          <Send className="w-4 h-4 mr-2" /> Send Newsletter
        </Button>
      </div>

      {/* Send Newsletter Dialog */}
      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Newsletter</DialogTitle></DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!emailForm.subject.trim() || !emailForm.body.trim()) { toast.error('Subject and body required'); return; }
            setSending(true);
            try {
              await emailAPI.sendNewsletter(emailForm);
              toast.success('Newsletter sent!');
              setShowSend(false);
              setEmailForm({ subject: '', body: '', template_id: '' });
              loadData();
            } catch { toast.error('Failed to send'); }
            finally { setSending(false); }
          }} className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Subject</label>
              <input value={emailForm.subject} onChange={e => setEmailForm({...emailForm, subject: e.target.value})} required className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white" placeholder="This week's top stories" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Body (HTML)</label>
              <textarea value={emailForm.body} onChange={e => setEmailForm({...emailForm, body: e.target.value})} required rows={6} className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white font-mono" placeholder="<h1>Weekly Newsletter</h1>" />
            </div>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={() => setShowSend(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1E5A8A] text-white" disabled={sending}>
                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send to {total} subscribers
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1E5A8A15] flex items-center justify-center">
                <Users className="w-5 h-5 text-[#1E5A8A]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-xs text-[#5B616E]">Total Subscribers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1F7A5A15] flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#1F7A5A]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{subscriptions.filter(s => s.is_active).length}</p>
                <p className="text-xs text-[#5B616E]">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#B3261E15] flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#B3261E]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{subscriptions.filter(s => !s.is_active).length}</p>
                <p className="text-xs text-[#5B616E]">Unsubscribed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscribers Table */}
      {loading ? (
        <div className="space-y-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
      ) : subscriptions.length === 0 ? (
        <Card className="bg-white border-[#E7E0D6] p-12 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
          <p className="text-[#5B616E]">No newsletter subscribers yet.</p>
        </Card>
      ) : (
        <Card className="bg-white border-[#E7E0D6] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E7E0D6] bg-[#FBF7EF]">
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3">Email</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden sm:table-cell">Name</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden md:table-cell">Categories</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden lg:table-cell">Subscribed</th>
                  <th className="text-right text-xs font-medium text-[#5B616E] px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => (
                  <tr key={sub.id} className="border-b border-[#E7E0D6] hover:bg-[#FBF7EF]/50 transition-colors">
                    <td className="px-4 py-3 text-sm">{sub.email}</td>
                    <td className="px-4 py-3 text-sm hidden sm:table-cell">{sub.name || '—'}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {sub.categories?.length > 0 ? sub.categories.map(c => (
                          <Badge key={c} variant="secondary" className="text-[10px]">{getCategoryName(c)}</Badge>
                        )) : <span className="text-xs text-[#5B616E]">All</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={sub.is_active ? 'default' : 'secondary'} className="text-[10px]">
                        {sub.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-[#5B616E]">
                      {new Date(sub.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleUnsubscribe(sub.email)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Email Send History */}
      {emailHistory.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Send History</h2>
          <div className="space-y-2">
            {emailHistory.slice(0, 10).map((email, i) => (
              <Card key={i} className="bg-white border-[#E7E0D6]">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{email.subject || 'Newsletter'}</p>
                      <p className="text-xs text-[#5B616E]">{email.sent_at ? new Date(email.sent_at).toLocaleString() : ''} &middot; {email.recipients || 0} recipients</p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{email.status || 'sent'}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
